import { CloseOutlined } from '@mui/icons-material';
import { Button } from 'finara-components';
import React, { useState, useEffect } from 'react';

import { Input } from '@/components/common/Input';
import type { UpdateOrganizationData } from '@/types/credits';

interface EditOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UpdateOrganizationData) => void;
  organizationName: string;
  organizationDescription?: string;
  isLoading?: boolean;
}

export const EditOrganizationModal: React.FC<EditOrganizationModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  organizationName,
  organizationDescription,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({ name: '', description: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: organizationName || '',
        description: organizationDescription || '',
      });
      setErrors({});
    }
  }, [isOpen, organizationName, organizationDescription]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors['name'] = 'El nombre / razón social es requerido';
    } else if (formData.name.length > 255) {
      newErrors['name'] = 'El nombre no debe exceder 255 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const submitData: UpdateOrganizationData = {};

    if (formData.name.trim() && formData.name !== organizationName) {
      submitData.name = formData.name.trim();
    }
    if (formData.description !== (organizationDescription || '')) {
      submitData.description = formData.description.trim();
    }

    onSubmit(submitData);
  };

  const handleClose = () => {
    setFormData({ name: '', description: '' });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <button
        className="absolute inset-0 bg-black opacity-60 cursor-default"
        onClick={handleClose}
        type="button"
        aria-label="Cerrar modal"
      />

      {/* Modal */}
      <div className="relative z-10 bg-surface border border-black/10 dark:border-white/15 rounded-lg shadow-2xl shadow-black/60 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-brand-white">Editar Organización</h2>
          <button
            onClick={handleClose}
            className="p-2 text-muted hover:text-brand-white transition-colors"
            disabled={isLoading}
          >
            <CloseOutlined />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="space-y-4">
            <div>
              <Input
                id="organization-name"
                label="Nombre / Razón Social"
                type="text"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Empresa Ejemplo S.A. de C.V."
                maxLength={255}
                required
              />
              {errors['name'] && <p className="text-sm text-red-400 mt-1">{errors['name']}</p>}
            </div>

            <div>
              <label
                htmlFor="organization-description"
                className="block text-sm font-medium text-brand-white mb-1"
              >
                Descripción (Opcional)
              </label>
              <textarea
                id="organization-description"
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Descripción de la organización"
                rows={3}
                maxLength={1000}
                className="w-full rounded-lg border border-border bg-surface text-brand-white placeholder:text-muted px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand-blue focus:border-brand-blue transition-colors disabled:opacity-50"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-border">
            <Button variant="white" onClick={handleClose} disabled={isLoading}>
              Cancelar
            </Button>
            <Button type="submit" variant="black" disabled={isLoading}>
              {isLoading ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
